import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { CreditCard, Plus, Trash2 } from 'lucide-react';
import { CustomSelect } from '../../../../ui/custom_react_select/custom_select';
import { TextInput } from '../../../../ui/input/text_input/text_input';
import type { IPriceOverride } from '../interfaces';

interface IPriceOverridesTableProps {
  overrides: IPriceOverride[];
  onRemoveOverride: (id: number) => void;
}

export const PriceOverridesTable: React.FC<IPriceOverridesTableProps> = ({
  overrides,
  onRemoveOverride,
}) => (
  <Paper variant="outlined" sx={{ borderRadius: 4, overflow: 'hidden' }}>
    <Box
      sx={{
        p: 3,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CreditCard size={20} color="#10b981" />
        <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
          Price Overrides
        </Typography>
      </Box>
      <Button
        startIcon={<Plus size={16} />}
        variant="text"
        size="small"
        sx={{
          color: 'primary.main',
          fontWeight: 800,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        Add Override
      </Button>
    </Box>

    <TableContainer>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell sx={{ fontWeight: 800, fontSize: '0.6875rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.5, px: 3 }}>Product</TableCell>
            <TableCell sx={{ fontWeight: 800, fontSize: '0.6875rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.5, px: 3 }}>Interval</TableCell>
            <TableCell sx={{ fontWeight: 800, fontSize: '0.6875rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.5, px: 3 }}>Company Size</TableCell>
            <TableCell sx={{ fontWeight: 800, fontSize: '0.6875rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.5, px: 3 }}>Amount</TableCell>
            <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.6875rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.5, px: 3 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {overrides.map((override) => (
            <TableRow key={override.id} hover>
              <TableCell sx={{ px: 3, py: 2 }}>
                <Box sx={{ minWidth: 200 }}>
                  <CustomSelect
                    name="product"
                    value={{ label: override.product, value: override.product }}
                    options={[{ label: override.product, value: override.product }]}
                    onChange={() => {}}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ px: 3, py: 2 }}>
                <Box sx={{ minWidth: 150 }}>
                  <CustomSelect
                    name="interval"
                    value={{ label: override.interval, value: override.interval }}
                    options={[{ label: 'Annual', value: 'Annual' }, { label: 'Monthly', value: 'Monthly' }]}
                    onChange={() => {}}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ px: 3, py: 2 }}>
                <Box sx={{ minWidth: 200 }}>
                  <CustomSelect
                    name="companySize"
                    value={{ label: override.companySize, value: override.companySize }}
                    options={[{ label: override.companySize, value: override.companySize }]}
                    onChange={() => {}}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ px: 3, py: 2 }}>
                <Box sx={{ position: 'relative' }}>
                  <TextInput
                    name="amount"
                    value={override.amount}
                    type="text"
                    onChange={() => {}}
                  />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ px: 3, py: 2 }}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onRemoveOverride(override.id)}
                  title="Remove Price Override"
                >
                  <Trash2 size={18} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);
